import {
  createContext,
  useContext,
  useState,
  useRef,
  useCallback,
  useEffect,
  ReactNode,
} from 'react'
import { useMutation } from '@apollo/client'
import { nhost } from '@/shared/lib/nhost'
import { useUserData } from '@nhost/react'
import { SAVE_PROGRESS } from './api'
import { audioStartPosition } from '@/shared/lib/audioStartPosition'

export type AudioArticle = {
  slug: string
  unitKey: string
  title: string
  audio: string
  duration?: number | null
  article_position: number
}

type AudioContextType = {
  audioRef: React.RefObject<HTMLAudioElement>
  isPlaying: boolean
  setIsPlaying: (playing: boolean) => void
  currentArticle: AudioArticle | null
  setCurrentArticle: (article: AudioArticle | null) => void
  articlesWithAudio: AudioArticle[]
  setArticlesWithAudio: (articles: AudioArticle[]) => void
  chapterSlug: string | null
  setChapterSlug: (slug: string | null) => void
  chapterTitle: string | null
  setChapterTitle: (title: string | null) => void
  showMiniPlayer: boolean
  setShowMiniPlayer: (show: boolean) => void
  currentTime: number
  setCurrentTime: (time: number) => void
  duration: number
  setDuration: (duration: number) => void
  togglePlay: () => void
  saveProgressAndClose: () => Promise<void>
  audioUrl: string | null
  startPlayingAt: (time: number) => void
  seekOnLoad: (time: number) => void
  setAutoPlayOnLoad: () => void
  onMiniPlayerClick: (() => void) | null
  setOnMiniPlayerClick: (callback: (() => void) | null) => void
  isReadingOverlayOpen: boolean
  setIsReadingOverlayOpen: (open: boolean) => void
  /**
   * Ob der Wiedereinstiegspunkt (current_chapter/article/audio_position) fuer das
   * gerade aktive Kapitel geschrieben werden darf. Wird von der Seite gesetzt, die das
   * Kapitel oeffnet - nur ein Aufruf aus "Meine Titel" darf ihn verschieben, sonst
   * wuerde zufaelliges Reinlesen ueber Entdecken o.ae. den Wiedereinstieg verschieben.
   */
  shouldSaveProgress: boolean
  setShouldSaveProgress: (value: boolean) => void
}

const AudioContext = createContext<AudioContextType | null>(null)

/**
 * Warum das Abspielen nicht zustande kam.
 *
 * Die drei Versuche, die nach dem Laden einer Datei von selbst starten (Vor,
 * Zurueck, automatisches Weiterspielen), haben ihren Fehler bisher verschluckt -
 * blieb es stumm, stand nichts in der Konsole. Der Browser lehnt aus zwei Gruenden
 * ab: die Datei laedt nicht (403 oder 404 aus dem Storage, "no supported sources")
 * oder seine Autoplay-Regel greift, weil der Start nicht unmittelbar aus einem
 * Tippen kommt ("NotAllowedError").
 */
function reportPlayFailure(err: unknown) {
  console.error('Audio play failed:', err)
}

export function AudioProvider({ children }: { children: ReactNode }) {
  const user = useUserData()
  const audioRef = useRef<HTMLAudioElement>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentArticle, setCurrentArticle] = useState<AudioArticle | null>(null)
  const [articlesWithAudio, setArticlesWithAudio] = useState<AudioArticle[]>([])
  const [chapterSlug, setChapterSlug] = useState<string | null>(null)
  const [chapterTitle, setChapterTitle] = useState<string | null>(null)
  const [showMiniPlayer, setShowMiniPlayer] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const pendingSeekTimeRef = useRef<number | null>(null)
  const shouldAutoPlayRef = useRef(false)
  // Zuletzt gehörte Position je Audiodatei, nur für diese Sitzung. Wer pausiert, eine
  // andere Datei anspielt oder die Ansicht schließt und später zurückkommt, hört dort
  // weiter - unabhängig davon, ob der Wiedereinstiegspunkt in der Datenbank gepflegt
  // wird (das passiert nur aus "Meine Titel", siehe shouldSaveProgress).
  const resumePositionsRef = useRef<Record<string, number>>({})
  const [onMiniPlayerClick, setOnMiniPlayerClick] = useState<(() => void) | null>(null)
  const [isReadingOverlayOpen, setIsReadingOverlayOpen] = useState(false)
  const [shouldSaveProgress, setShouldSaveProgress] = useState(false)
  const shouldSaveProgressRef = useRef(shouldSaveProgress)
  useEffect(() => {
    shouldSaveProgressRef.current = shouldSaveProgress
  }, [shouldSaveProgress])

  const [saveProgress] = useMutation(SAVE_PROGRESS)

  const audioUrl = currentArticle?.audio
    ? nhost.storage.getPublicUrl({ fileId: currentArticle.audio })
    : null

  const togglePlay = useCallback(async () => {
    const audio = audioRef.current
    if (!audio) return
    // Use actual audio state to avoid sync issues
    if (audio.paused) {
      // Fehler sichtbar machen statt als unbehandelte Promise-Rejection zu verlieren
      // (z.B. wenn die Datei nicht laedt: 'The element has no supported sources.').
      audio.play().catch((err) => console.error('Audio play failed:', audio.currentSrc, err))
    } else {
      audio.pause()
      // Save progress when pausing from mini player
      if (shouldSaveProgressRef.current && user?.id && chapterSlug && currentArticle?.slug) {
        try {
          await saveProgress({
            variables: {
              userId: user.id,
              chapterSlug,
              articleSlug: currentArticle.slug,
              audioPosition: Math.floor(audio.currentTime),
            },
          })
        } catch (err) {
          console.error('Failed to save progress on pause:', err)
        }
      }
    }
  }, [user?.id, chapterSlug, currentArticle?.slug, saveProgress])

  const startPlayingAt = useCallback((time: number) => {
    pendingSeekTimeRef.current = time
    shouldAutoPlayRef.current = true
    if (audioRef.current && audioRef.current.readyState >= 1) {
      audioRef.current.currentTime = time
      audioRef.current.play().catch(reportPlayFailure)
      setIsPlaying(true)
      pendingSeekTimeRef.current = null
      shouldAutoPlayRef.current = false
    }
  }, [])

  /**
   * Startposition für die Datei, die als Nächstes geladen wird.
   *
   * Die Quelle wechselt erst im nächsten Render, darum lässt sich hier noch nichts
   * spulen - hinterlegt wird die Absicht. Sie schlägt beim Laden die gemerkte
   * Hörposition aus (siehe `audioStartPosition`), weshalb der Vor- und der
   * Zurück-Knopf sie mit 0 setzen.
   */
  const seekOnLoad = useCallback((time: number) => {
    pendingSeekTimeRef.current = time
  }, [])

  // Set auto-play flag for when the next audio loads (doesn't try to play immediately)
  const setAutoPlayOnLoad = useCallback(() => {
    shouldAutoPlayRef.current = true
  }, [])

  const saveProgressAndClose = useCallback(async () => {
    if (audioRef.current) {
      audioRef.current.pause()
    }
    setIsPlaying(false)

    if (shouldSaveProgressRef.current && user?.id && chapterSlug) {
      try {
        await saveProgress({
          variables: {
            userId: user.id,
            chapterSlug,
            articleSlug: currentArticle?.slug || null,
            audioPosition: audioRef.current?.currentTime
              ? Math.floor(audioRef.current.currentTime)
              : null,
          },
        })
      } catch (err) {
        console.error('Failed to save progress:', err)
      }
    }

    setShowMiniPlayer(false)
    setCurrentArticle(null)
    setChapterSlug(null)
    setChapterTitle(null)
  }, [user?.id, chapterSlug, currentArticle?.slug, saveProgress])

  // Refs to access current values in event handlers without re-running effect
  const articlesWithAudioRef = useRef(articlesWithAudio)
  const currentArticleRef = useRef(currentArticle)
  const chapterSlugRef = useRef(chapterSlug)
  const userRef = useRef(user)
  // Auch die Mutation läuft über eine Ref: Der Effekt unten hängt bewusst nur an
  // `audioUrl` und darf die Audio-Listener nicht neu aufsetzen, bloß weil Apollo eine
  // neue `mutate`-Funktion liefert.
  const saveProgressRef = useRef(saveProgress)
  useEffect(() => {
    saveProgressRef.current = saveProgress
  }, [saveProgress])
  useEffect(() => {
    articlesWithAudioRef.current = articlesWithAudio
  }, [articlesWithAudio])
  useEffect(() => {
    currentArticleRef.current = currentArticle
  }, [currentArticle])
  useEffect(() => {
    chapterSlugRef.current = chapterSlug
  }, [chapterSlug])
  useEffect(() => {
    userRef.current = user
  }, [user])

  useEffect(() => {
    const audio = audioRef.current
    if (!audio || !audioUrl) return

    const rememberPosition = () => {
      if (audio.ended) return
      resumePositionsRef.current[audioUrl] = audio.currentTime
    }
    /**
     * Spult die gerade geladene Datei an ihren Startpunkt: an die ausdrücklich
     * vorgegebene Stelle, sonst an die gemerkte. Die Absicht gilt nur für diese eine
     * Datei und wird darum verbraucht.
     */
    const applyStartPosition = () => {
      const start = audioStartPosition({
        pending: pendingSeekTimeRef.current,
        remembered: resumePositionsRef.current[audioUrl],
        duration: audio.duration,
      })
      pendingSeekTimeRef.current = null
      if (start === null) return
      audio.currentTime = start
      setCurrentTime(start)
    }

    const handleTimeUpdate = () => setCurrentTime(audio.currentTime)
    const handleLoadedMetadata = () => {
      setDuration(audio.duration)
      applyStartPosition()
      if (shouldAutoPlayRef.current) {
        audio.play().catch(reportPlayFailure)
        setIsPlaying(true)
        shouldAutoPlayRef.current = false
      }
    }
    const handlePlay = () => setIsPlaying(true)
    const handlePause = () => {
      rememberPosition()
      setIsPlaying(false)
    }
    const handleEnded = () => {
      // Zu Ende gehört: beim nächsten Mal wieder von vorn.
      delete resumePositionsRef.current[audioUrl]
      // Auto-play next audio when current one ends
      const articles = articlesWithAudioRef.current
      const current = currentArticleRef.current
      if (!current || articles.length === 0) return

      const currentIndex = articles.findIndex((a) => a.unitKey === current.unitKey)
      if (currentIndex >= 0 && currentIndex < articles.length - 1) {
        const nextArticle = articles[currentIndex + 1]
        // Reset currentTime immediately to avoid progress bar flickering
        setCurrentTime(0)
        // Die nächste Datei fängt von vorn an. Ohne diese Vorgabe würde sie dort
        // einsetzen, wo sie in dieser Sitzung zuletzt verlassen wurde - beim
        // Durchhören eines Kapitels sprang der Ton dann mitten in den Abschnitt.
        pendingSeekTimeRef.current = 0
        shouldAutoPlayRef.current = true
        setCurrentArticle(nextArticle)

        // Save progress to database so reading page scrolls to correct article
        const currentUser = userRef.current
        const currentChapterSlug = chapterSlugRef.current
        if (shouldSaveProgressRef.current && currentUser?.id && currentChapterSlug) {
          saveProgressRef
            .current({
              variables: {
                userId: currentUser.id,
                chapterSlug: currentChapterSlug,
                articleSlug: nextArticle.slug,
                audioPosition: 0,
              },
            })
            .catch((err) => console.error('Failed to save progress on auto-play:', err))
        }
      }
    }

    audio.addEventListener('timeupdate', handleTimeUpdate)
    audio.addEventListener('loadedmetadata', handleLoadedMetadata)
    audio.addEventListener('play', handlePlay)
    audio.addEventListener('pause', handlePause)
    audio.addEventListener('ended', handleEnded)

    // Handle case where metadata already loaded (e.g., from cache) before listener was attached
    if (audio.readyState >= 1) {
      setDuration(audio.duration)
      applyStartPosition()
      if (shouldAutoPlayRef.current) {
        audio.play().catch(reportPlayFailure)
        setIsPlaying(true)
        shouldAutoPlayRef.current = false
      }
    }

    return () => {
      // Wechsel der Datei oder Abbau des Elements: Stand sichern, bevor er verloren geht.
      rememberPosition()
      audio.removeEventListener('timeupdate', handleTimeUpdate)
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata)
      audio.removeEventListener('play', handlePlay)
      audio.removeEventListener('pause', handlePause)
      audio.removeEventListener('ended', handleEnded)
    }
  }, [audioUrl])

  return (
    <AudioContext.Provider
      value={{
        audioRef,
        isPlaying,
        setIsPlaying,
        currentArticle,
        setCurrentArticle,
        articlesWithAudio,
        setArticlesWithAudio,
        chapterSlug,
        setChapterSlug,
        chapterTitle,
        setChapterTitle,
        showMiniPlayer,
        setShowMiniPlayer,
        currentTime,
        setCurrentTime,
        duration,
        setDuration,
        togglePlay,
        saveProgressAndClose,
        audioUrl,
        startPlayingAt,
        seekOnLoad,
        setAutoPlayOnLoad,
        onMiniPlayerClick,
        setOnMiniPlayerClick,
        isReadingOverlayOpen,
        setIsReadingOverlayOpen,
        shouldSaveProgress,
        setShouldSaveProgress,
      }}
    >
      {children}
      {audioUrl && (
        <audio key={audioUrl} ref={audioRef} src={audioUrl} style={{ display: 'none' }} />
      )}
    </AudioContext.Provider>
  )
}

export function useAudio() {
  const context = useContext(AudioContext)
  if (!context) {
    throw new Error('useAudio must be used within an AudioProvider')
  }
  return context
}
